import React, { useCallback, useEffect, useState } from 'react';
import particularesApiService from '../services/ParticularesApiService';
import { AulaEmptyState, AulaSection } from './common/AulaShell';
import { StudentModal } from './common/StudentShell';
import { formatHorariosResumen } from './ParticularCursoHorarioModal';

// "Cursos" del alumno particular: sus cursos matriculados y un buscador para encontrar otros cursos
// (por nombre, descripción o etiqueta) y matricularse con el código que le da el docente.

const getValue = (source, lower, upper) => source?.[lower] ?? source?.[upper];
const getList = (source, lower, upper) => {
  const value = getValue(source, lower, upper);
  return Array.isArray(value) ? value : [];
};

const normalizeCurso = (item) => ({
  id: String(getValue(item, 'id', 'Id') || ''),
  nombre: getValue(item, 'nombre', 'Nombre') || 'Curso',
  descripcion: getValue(item, 'descripcion', 'Descripcion') || '',
  matriculado: Boolean(getValue(item, 'matriculado', 'Matriculado')),
  matriculacionAbierta: Boolean(getValue(item, 'matriculacionAbierta', 'MatriculacionAbierta')),
  etiquetas: getList(item, 'etiquetas', 'Etiquetas').map((etiqueta) => ({
    id: String(getValue(etiqueta, 'id', 'Id') || ''),
    nombre: getValue(etiqueta, 'nombre', 'Nombre') || '',
    color: getValue(etiqueta, 'color', 'Color') || '#2563eb'
  })),
  profesores: getList(item, 'profesores', 'Profesores'),
  horarios: getList(item, 'horarios', 'Horarios').map((h) => ({
    diaSemana: Number(getValue(h, 'diaSemana', 'DiaSemana')),
    horaInicio: getValue(h, 'horaInicio', 'HoraInicio') || '',
    horaFin: getValue(h, 'horaFin', 'HoraFin') || ''
  }))
});

const SEARCH_MIN_LENGTH = 2;

// aulasPorCurso: Map(cursoId -> aulas) con las aulas a las que el alumno tiene acceso (vienen del panel).
const ParticularAlumnoCursos = ({ onMatriculado, aulasPorCurso, onOpenAula }) => {
  const [matriculados, setMatriculados] = useState([]);
  const [resultados, setResultados] = useState([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [searching, setSearching] = useState(false);
  const [error, setError] = useState('');
  const [matricularCurso, setMatricularCurso] = useState(null);
  const [codigo, setCodigo] = useState('');
  const [matricularError, setMatricularError] = useState('');
  const [matriculando, setMatriculando] = useState(false);
  const [aviso, setAviso] = useState('');

  const load = useCallback(async (term) => {
    try {
      const response = await particularesApiService.getAlumnoCursos({ search: term });
      setMatriculados(getList(response, 'matriculados', 'Matriculados').map(normalizeCurso));
      setResultados(getList(response, 'resultados', 'Resultados').map(normalizeCurso));
      setError('');
    } catch (err) {
      setError(err.message || 'No pudimos cargar los cursos.');
    }
  }, []);

  useEffect(() => {
    setLoading(true);
    load('').finally(() => setLoading(false));
  }, [load]);

  // Busca mientras se escribe, con una pequeña espera para no consultar en cada tecla.
  useEffect(() => {
    const term = search.trim();
    if (term.length < SEARCH_MIN_LENGTH) {
      setResultados([]);
      setSearching(false);
      return undefined;
    }

    setSearching(true);
    const timer = setTimeout(() => {
      load(term).finally(() => setSearching(false));
    }, 350);
    return () => clearTimeout(timer);
  }, [load, search]);

  const openMatricular = (curso) => {
    setMatricularCurso(curso);
    setCodigo('');
    setMatricularError('');
  };

  const closeMatricular = () => {
    if (matriculando) return;
    setMatricularCurso(null);
  };

  const confirmMatricular = async (event) => {
    event.preventDefault();
    if (!codigo.trim()) {
      setMatricularError('Ingresá el código que te dio tu docente.');
      return;
    }

    setMatriculando(true);
    setMatricularError('');
    try {
      await particularesApiService.matricularAlumnoCurso(matricularCurso.id, codigo);
      setAviso(`Te matriculaste en ${matricularCurso.nombre}.`);
      setMatricularCurso(null);
      await load(search.trim().length >= SEARCH_MIN_LENGTH ? search.trim() : '');
      onMatriculado?.();
    } catch (err) {
      setMatricularError(err.message || 'No pudimos matricularte en el curso.');
    } finally {
      setMatriculando(false);
    }
  };

  const renderAulas = (curso) => {
    const aulas = aulasPorCurso?.get(curso.id) || [];
    if (aulas.length === 0) {
      return <p className="sp-course-empty sp-curso-aulas-empty">Este curso todavía no tiene aulas.</p>;
    }

    return (
      <ul className="sp-aula-list sp-curso-aulas">
        {aulas.map((aula) => {
          const aulaId = String(getValue(aula, 'id', 'Id') || '');
          const detalle = getValue(aula, 'descripcion', 'Descripcion') || getValue(aula, 'nicknameUrl', 'NicknameUrl') || '';
          return (
            <li key={aulaId}>
              <button type="button" className="sp-aula-row" onClick={() => onOpenAula?.(aula)}>
                <span className="sp-aula-icon">
                  <i className="fas fa-chalkboard" aria-hidden="true" />
                </span>
                <span className="sp-aula-copy">
                  <strong>{getValue(aula, 'nombre', 'Nombre') || 'Aula'}</strong>
                  {detalle && <small>{detalle}</small>}
                </span>
                <span className="sp-aula-go">
                  <span>Ingresar</span>
                  <i className="fas fa-arrow-right" aria-hidden="true" />
                </span>
              </button>
            </li>
          );
        })}
      </ul>
    );
  };

  const renderCurso = (curso) => (
    <article className="sp-curso" key={curso.id}>
      <div className="sp-curso-main">
        <strong>{curso.nombre}</strong>
        {curso.descripcion && <p>{curso.descripcion}</p>}
        <div className="sp-curso-meta">
          {curso.profesores.length > 0 && (
            <span><i className="fas fa-chalkboard-teacher" aria-hidden="true" /> {curso.profesores.join(', ')}</span>
          )}
          {curso.horarios.length > 0 && (
            <span><i className="far fa-clock" aria-hidden="true" /> {formatHorariosResumen(curso.horarios)}</span>
          )}
        </div>
        {curso.etiquetas.length > 0 && (
          <div className="sp-curso-tags">
            {curso.etiquetas.map((etiqueta) => (
              <span key={etiqueta.id} style={{ '--tag-color': etiqueta.color }}>{etiqueta.nombre}</span>
            ))}
          </div>
        )}
        {curso.matriculado && renderAulas(curso)}
      </div>
      <div className="sp-curso-action">
        {curso.matriculado ? (
          <span className="sp-curso-badge"><i className="fas fa-check" aria-hidden="true" /> Matriculado</span>
        ) : curso.matriculacionAbierta ? (
          <button type="button" className="btn-primary" onClick={() => openMatricular(curso)}>
            Matricularse
          </button>
        ) : (
          <span className="sp-curso-hint">La inscripción la hace tu docente</span>
        )}
      </div>
    </article>
  );

  const term = search.trim();

  return (
    <div className="sp-cursos">
      {aviso && (
        <div className="sp-cursos-aviso" role="status">
          <i className="fas fa-check-circle" aria-hidden="true" />
          <span>{aviso}</span>
          <button type="button" onClick={() => setAviso('')} aria-label="Cerrar aviso">
            <i className="fas fa-times" aria-hidden="true" />
          </button>
        </div>
      )}

      <AulaSection
        active
        icon="fa-search"
        title="Buscar cursos"
        subtitle="Buscá por nombre, tema o etiqueta. Para matricularte necesitás el código que te da tu docente."
      >
        <div className="sp-search sp-cursos-search">
          <i className="fas fa-search" aria-hidden="true" />
          <input
            type="text"
            placeholder="Ej.: inglés, matemática, ingreso..."
            aria-label="Buscar cursos"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
          />
        </div>

        {term.length > 0 && term.length < SEARCH_MIN_LENGTH && (
          <p className="sp-cursos-hint">Escribí al menos {SEARCH_MIN_LENGTH} letras para buscar.</p>
        )}
        {term.length >= SEARCH_MIN_LENGTH && (
          searching ? (
            <AulaEmptyState icon="fa-spinner fa-spin">Buscando cursos...</AulaEmptyState>
          ) : resultados.length === 0 ? (
            <AulaEmptyState icon="fa-search">
              No encontramos cursos con “{term}” en los que no estés matriculado.
            </AulaEmptyState>
          ) : (
            <div className="sp-curso-list">{resultados.map(renderCurso)}</div>
          )
        )}
      </AulaSection>

      <AulaSection
        active
        icon="fa-layer-group"
        title="Mis cursos"
        subtitle={loading ? 'Cargando...' : `${matriculados.length} curso${matriculados.length === 1 ? '' : 's'} matriculado${matriculados.length === 1 ? '' : 's'}`}
      >
        {error && <AulaEmptyState icon="fa-exclamation-triangle">{error}</AulaEmptyState>}
        {!error && !loading && matriculados.length === 0 && (
          <AulaEmptyState icon="fa-layer-group">Todavía no estás matriculado en ningún curso.</AulaEmptyState>
        )}
        {!error && matriculados.length > 0 && (
          <div className="sp-curso-list">{matriculados.map(renderCurso)}</div>
        )}
      </AulaSection>

      {matricularCurso && (
        <StudentModal
          eyebrow="Matricularme"
          title={matricularCurso.nombre}
          subtitle="Ingresá el código de matriculación que te dio tu docente."
          onClose={closeMatricular}
          onSubmit={confirmMatricular}
          footer={(
            <>
              <button type="button" className="btn-secondary" onClick={closeMatricular} disabled={matriculando}>
                Cancelar
              </button>
              <button type="submit" className="btn-primary" disabled={matriculando}>
                <i className={`fas ${matriculando ? 'fa-spinner fa-spin' : 'fa-check'}`} aria-hidden="true" />
                Matricularme
              </button>
            </>
          )}
        >
          <label className="sp-field">
            Código de matriculación
            <input
              type="text"
              value={codigo}
              onChange={(event) => setCodigo(event.target.value.toUpperCase())}
              placeholder="XXXX-XXXX"
              autoComplete="off"
              autoFocus
              maxLength={20}
              disabled={matriculando}
              className="sp-codigo-input"
            />
          </label>
          {matricularError && <p className="sp-cursos-error" role="alert">{matricularError}</p>}
        </StudentModal>
      )}
    </div>
  );
};

export default ParticularAlumnoCursos;
